/**
 * Sistema de Logging Seguro para Produção
 * 
 * Este sistema garante que dados sensíveis não sejam logados em produção
 * e fornece diferentes níveis de logging baseados no ambiente.
 */

// Tipos de log disponíveis
export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

// Configuração do ambiente
// Safe check for __DEV__ to prevent crashes in Node.js environment (e.g., Stripe Webhook)
const isDevGlobal = typeof global !== 'undefined' && (global as any).__DEV__;
const isDevWindow = typeof window !== 'undefined' && (window as any).__DEV__;
const isDevGeneric = typeof __DEV__ !== 'undefined' && __DEV__;

const isDevelopment = process.env.NODE_ENV === 'development' || isDevGlobal || isDevWindow || isDevGeneric;
const isProduction = process.env.NODE_ENV === 'production';

// Lista de campos sensíveis que nunca devem ser logados
const SENSITIVE_FIELDS = [
  'password', 'senha', 'token', 'apiKey', 'secret', 'key', 'auth',
  'email', 'cpf', 'telefone', 'chavePix', 'cardNumber', 'cvv',
  'privateKey', 'clientSecret', 'accessToken', 'refreshToken',
  'sessionId', 'userId', 'firebaseConfig', 'stripeKey'
];

// Função para sanitizar objetos removendo campos sensíveis
function sanitizeData(data: any): any {
  if (data === null || data === undefined) {
    return data;
  }

  if (typeof data === 'string') {
    // Mascarar emails
    if (data.includes('@')) {
      const [local, domain] = data.split('@');
      return `${local.substring(0, 2)}***@${domain}`;
    }
    return data;
  }

  if (typeof data === 'object') {
    if (Array.isArray(data)) {
      return data.map(item => sanitizeData(item));
    }

    const sanitized: any = {};
    for (const [key, value] of Object.entries(data)) {
      const lowerKey = key.toLowerCase();

      // Verificar se é um campo sensível
      const isSensitive = SENSITIVE_FIELDS.some(field =>
        lowerKey.includes(field.toLowerCase())
      );

      if (isSensitive) {
        sanitized[key] = '[REDACTED]';
      } else {
        sanitized[key] = sanitizeData(value);
      }
    }
    return sanitized;
  }

  return data;
}

// Função principal de logging
function log(level: LogLevel, message: string, data?: any): void {
  // Em produção, só logamos erros e warnings críticos
  if (isProduction && (level === 'debug' || level === 'info')) {
    return;
  }

  const timestamp = new Date().toISOString();
  const sanitizedData = data ? sanitizeData(data) : undefined;

  const logEntry = {
    timestamp,
    level: level.toUpperCase(),
    message,
    ...(sanitizedData && { data: sanitizedData })
  };

  switch (level) {
    case 'debug':
      if (isDevelopment) {
        console.log(`🔍 [DEBUG] ${message}`, sanitizedData || '');
      }
      break;
    case 'info':
      if (isDevelopment) {
        console.log(`ℹ️ [INFO] ${message}`, sanitizedData || '');
      }
      break;
    case 'warn':
      console.warn(`⚠️ [WARN] ${message}`, sanitizedData || '');
      break;
    case 'error':
      console.error(`❌ [ERROR] ${message}`, sanitizedData || '');
      break;
  }
}

// Funções de conveniência
export const logger = {
  debug: (message: string, data?: any) => log('debug', message, data),
  info: (message: string, data?: any) => log('info', message, data),
  warn: (message: string, data?: any) => log('warn', message, data),
  error: (message: string, data?: any) => log('error', message, data),

  // Função especial para erros de autenticação
  authError: (message: string, error?: any) => {
    const sanitizedError = error ? {
      message: error.message,
      code: error.code,
      // Não incluir stack trace em produção
      ...(isDevelopment && { stack: error.stack })
    } : undefined;

    log('error', `[AUTH] ${message}`, sanitizedError);
  },

  // Função especial para erros de pagamento
  paymentError: (message: string, error?: any) => {
    const sanitizedError = error ? {
      message: error.message,
      type: error.type,
      // Não incluir detalhes sensíveis do Stripe
      ...(isDevelopment && { code: error.code })
    } : undefined;

    log('error', `[PAYMENT] ${message}`, sanitizedError);
  },

  // Função para logs de performance (apenas em desenvolvimento)
  performance: (operation: string, duration: number) => {
    if (isDevelopment) {
      log('debug', `[PERFORMANCE] ${operation} took ${duration}ms`);
    }
  }
};

// Função para substituir console.log em produção
export function replaceConsoleInProduction(): void {
  if (isProduction) {
    // Substituir console.log por função vazia em produção
    console.log = () => { };
    console.debug = () => { };

    // Manter apenas warn e error, mas sanitizados
    const originalWarn = console.warn;
    const originalError = console.error;

    console.warn = (message: any, ...args: any[]) => {
      const sanitizedArgs = args.map(arg => sanitizeData(arg));
      originalWarn(sanitizeData(message), ...sanitizedArgs);
    };

    console.error = (message: any, ...args: any[]) => {
      const sanitizedArgs = args.map(arg => sanitizeData(arg));
      originalError(sanitizeData(message), ...sanitizedArgs);
    };
  }
}

export default logger;